// Logika murni sprint /dev — TANPA efek samping (no pg, no DOM, no fetch).
// Aman dipakai di client & server, dan bisa diuji lewat scripts/test-dev-sprint.mjs.
// Semua tanggal berbasis string YYYY-MM-DD (UTC) supaya deterministik.

import type { Task, Sprint, Project, TaskStatus } from '@/lib/types'

export const VALID_POINTS = [1, 2, 3, 5, 8] as const
export const DAY_MS = 86400000

/** Status efektif sebuah task — dihitung caller (dari subtask) lalu diinjeksi. */
export type StatusOf = (t: Task) => TaskStatus

const round1 = (x: number): number => Math.round(x * 10) / 10
const norm = (x?: string | null): string => (x ?? '').trim().toLowerCase()

// ── Helper tanggal (UTC, YYYY-MM-DD) ─────────────────────────────────────────
export const isoDay = (v?: string | null): string => (v ? String(v).slice(0, 10) : '')

const dayNum = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return NaN
  return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS)
}

/** Selisih hari toISO − fromISO (bisa negatif). */
export const diffDays = (fromISO: string, toISO: string): number => dayNum(toISO) - dayNum(fromISO)

/** Tambah n hari ke tanggal ISO → YYYY-MM-DD. */
export const addDays = (iso: string, n: number): string =>
  new Date((dayNum(iso) + n) * DAY_MS).toISOString().slice(0, 10)

// ── Points ───────────────────────────────────────────────────────────────────
export const pointsOf = (t: Pick<Task, 'points'>): number => {
  const p = t.points
  return typeof p === 'number' && p > 0 ? p : 1
}

// ── Sprint ↔ task (hybrid: bucket lewat window tanggal) ──────────────────────
/** Jumlah minggu dalam sprint (dibulatkan ke atas per 7 hari, minimal 1). */
export const sprintWeeks = (s: Sprint): number =>
  Math.max(1, Math.ceil((diffDays(isoDay(s.startDate), isoDay(s.endDate)) + 1) / 7))

/** Task masuk sprint: EKSPLISIT via sprintId; kalau null → fallback window tanggal (legacy). */
export const taskInSprint = (t: Pick<Task, 'dueDate' | 'sprintId'>, s: Sprint): boolean => {
  if (t.sprintId != null && t.sprintId !== '') return t.sprintId === s.id
  const due = isoDay(t.dueDate)
  if (!due) return false
  return due >= isoDay(s.startDate) && due <= isoDay(s.endDate)
}

/** Index minggu (1-based) sebuah dueDate dalam sprint, atau null bila di luar. */
export const weekOfSprint = (dueDate: string | null | undefined, s: Sprint): number | null => {
  const due = isoDay(dueDate)
  if (!due || due < isoDay(s.startDate) || due > isoDay(s.endDate)) return null
  const idx = Math.floor(diffDays(isoDay(s.startDate), due) / 7) + 1
  return Math.min(Math.max(idx, 1), sprintWeeks(s))
}

/** Tanggal awal minggu ke-w (1-based) dalam sprint → YYYY-MM-DD. */
export const weekStartDate = (s: Sprint, w: number): string => addDays(isoDay(s.startDate), (w - 1) * 7)

/** Sprint aktif = memuat hari ini; fallback ke sprint lampau terbaru, lalu terdekat mendatang. */
export const activeSprint = (sprints: Sprint[], todayISO: string): Sprint | null => {
  const today = isoDay(todayISO)
  const containing = sprints.find((s) => today >= isoDay(s.startDate) && today <= isoDay(s.endDate))
  if (containing) return containing
  const past = sprints
    .filter((s) => isoDay(s.endDate) < today)
    .sort((a, b) => isoDay(b.endDate).localeCompare(isoDay(a.endDate)))
  if (past[0]) return past[0]
  const future = sprints
    .filter((s) => isoDay(s.startDate) > today)
    .sort((a, b) => isoDay(a.startDate).localeCompare(isoDay(b.startDate)))
  return future[0] ?? null
}

/** True bila dua sprint beririsan tanggal (dipakai validasi non-overlap saat create). */
export const sprintsOverlap = (a: { startDate: string; endDate: string }, b: { startDate: string; endDate: string }): boolean =>
  isoDay(a.startDate) <= isoDay(b.endDate) && isoDay(b.startDate) <= isoDay(a.endDate)

// ── Burndown ─────────────────────────────────────────────────────────────────
export interface BurndownPoint { day: string; ideal: number; actual: number | null }

export const burndown = (s: Sprint, tasks: Task[], statusOf: StatusOf, todayISO: string): BurndownPoint[] => {
  const inSprint = tasks.filter((t) => taskInSprint(t, s))
  const total = inSprint.reduce((a, t) => a + pointsOf(t), 0)
  const start = isoDay(s.startDate)
  const span = Math.max(1, diffDays(start, isoDay(s.endDate)))
  const today = isoDay(todayISO)
  const out: BurndownPoint[] = []
  for (let d = 0; d <= span; d++) {
    const day = addDays(start, d)
    const ideal = round1(total - (total * d) / span)
    let actual: number | null = null
    if (day <= today) {
      const done = inSprint.reduce((a, t) => {
        if (statusOf(t) !== 'done') return a
        const cd = isoDay(t.tanggalSelesai) || isoDay(t.dueDate) || start
        return cd <= day ? a + pointsOf(t) : a
      }, 0)
      actual = total - done
    }
    out.push({ day, ideal, actual })
  }
  return out
}

// ── Velocity ─────────────────────────────────────────────────────────────────
export interface VelocityPoint { sprintId: string; name: string; points: number; current: boolean }

export const velocity = (sprints: Sprint[], tasks: Task[], statusOf: StatusOf, todayISO: string): VelocityPoint[] => {
  const today = isoDay(todayISO)
  return sprints
    .slice()
    .sort((a, b) => isoDay(a.startDate).localeCompare(isoDay(b.startDate)))
    .map((s) => {
      // Pakai keanggotaan sprint (sprint_id) — hindari double-count antar sprint yang overlap.
      const points = tasks.reduce((a, t) => (statusOf(t) === 'done' && taskInSprint(t, s) ? a + pointsOf(t) : a), 0)
      const current = today >= isoDay(s.startDate) && today <= isoDay(s.endDate)
      return { sprintId: s.id, name: s.name, points, current }
    })
}

// ── Workload (dev × minggu, points task OPEN) ────────────────────────────────
export const workload = (
  tasks: Task[],
  s: Sprint,
  usernames: string[],
  statusOf: StatusOf,
  fallbackWeek = 1, // task anggota sprint tapi tanpa minggu (deadline kosong) → masuk minggu ini
): Record<string, number[]> => {
  const weeks = sprintWeeks(s)
  const map: Record<string, number[]> = {}
  for (const u of usernames) map[norm(u)] = new Array(weeks).fill(0)
  for (const t of tasks) {
    if (statusOf(t) === 'done') continue
    const w = weekOfSprint(t.dueDate, s) ?? fallbackWeek
    const wi = Math.min(Math.max(w, 1), weeks)
    const key = norm(t.assignee)
    if (!(key in map)) continue
    map[key][wi - 1] += pointsOf(t)
  }
  return map
}

// ── On-time scorecard per dev ────────────────────────────────────────────────
export interface Scorecard {
  onTimePct: number | null // null = belum ada task selesai
  avgSlip: number // rata-rata hari telat (hanya yang telat)
  committed: number // total points assigned
  done: number // points selesai
  openCount: number // jumlah task open
}

export const onTimeScore = (tasks: Task[], username: string, statusOf: StatusOf): Scorecard => {
  const mine = tasks.filter((t) => norm(t.assignee) === norm(username))
  let committed = 0, done = 0, openCount = 0, onTime = 0, doneCount = 0, slipSum = 0
  for (const t of mine) {
    const p = pointsOf(t)
    committed += p
    if (statusOf(t) === 'done') {
      done += p
      doneCount++
      const due = isoDay(t.dueDate)
      const fin = isoDay(t.tanggalSelesai) || due
      if (due && fin) {
        if (fin <= due) onTime++
        else slipSum += diffDays(due, fin)
      } else {
        onTime++ // tanpa deadline → anggap on-time
      }
    } else {
      openCount++
    }
  }
  return {
    onTimePct: doneCount ? Math.round((onTime / doneCount) * 100) : null,
    avgSlip: doneCount ? round1(slipSum / doneCount) : 0,
    committed,
    done,
    openCount,
  }
}

// ── Risk radar ───────────────────────────────────────────────────────────────
export type RiskKind = 'late' | 'blocked' | 'due-soon' | 'overload' | 'aging'
export const RISK_RANK: Record<RiskKind, number> = { late: 0, blocked: 1, 'due-soon': 2, overload: 3, aging: 4 }
export const RISK_LABEL: Record<RiskKind, string> = {
  late: 'Telat',
  blocked: 'Keblok',
  'due-soon': 'Due ≤3 hari',
  overload: 'Overload',
  aging: 'Mandek',
}

export interface RiskCtx {
  todayISO: string
  statusOf: StatusOf
  isProjectBlocked: (projectId: string) => boolean
  overloadedUsers: Set<string> // username ter-normalisasi yang over kapasitas minggu ini
}

/** Flag risiko sebuah task (kosong bila sudah done), urut dari paling parah. */
export const riskFlags = (t: Task, ctx: RiskCtx): RiskKind[] => {
  if (ctx.statusOf(t) === 'done') return []
  const flags: RiskKind[] = []
  const today = isoDay(ctx.todayISO)
  const due = isoDay(t.dueDate)
  if (due && due < today) flags.push('late')
  if (ctx.isProjectBlocked(t.projectId)) flags.push('blocked')
  if (due && due >= today && diffDays(today, due) <= 3) flags.push('due-soon')
  if (ctx.overloadedUsers.has(norm(t.assignee))) flags.push('overload')
  if (ctx.statusOf(t) === 'in-progress') {
    const created = isoDay(t.createdAt)
    if (created && diffDays(created, today) > 7) flags.push('aging')
  }
  return flags.sort((a, b) => RISK_RANK[a] - RISK_RANK[b])
}

// ── Sprint health ────────────────────────────────────────────────────────────
export type Health = 'on-track' | 'at-risk' | 'behind'

export const sprintHealth = (s: Sprint, tasks: Task[], ctx: RiskCtx): Health => {
  const inSprint = tasks.filter((t) => taskInSprint(t, s))
  let behind = false
  let atRisk = false
  for (const t of inSprint) {
    const f = riskFlags(t, ctx)
    if (f.includes('late') || f.includes('blocked')) behind = true
    else if (f.length) atRisk = true
  }
  return behind ? 'behind' : atRisk ? 'at-risk' : 'on-track'
}

// ── Kanban (dengan kolom Review) ─────────────────────────────────────────────
export type KanbanCol = 'todo' | 'inprogress' | 'review' | 'done'
export const KANBAN_COLS: { key: KanbanCol; label: string }[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'inprogress', label: 'In Progress' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
]
/** Kolom kanban sebuah task: done>review(flag)>status. */
export const kanbanColumn = (t: Pick<Task, 'inReview'>, eff: TaskStatus): KanbanCol => {
  if (eff === 'done') return 'done'
  if (t.inReview) return 'review'
  if (eff === 'not-started') return 'todo'
  return 'inprogress'
}

// ── Project progress (view Project: mulai → target selesai → progress mingguan) ──
/** Index minggu ke-n (1-based, kontinu) sebuah tanggal relatif start project. */
export const weekOfProject = (dateISO: string | null | undefined, startISO: string): number | null => {
  const d = isoDay(dateISO)
  const s = isoDay(startISO)
  if (!d || !s) return null
  return Math.max(1, Math.floor(diffDays(s, d) / 7) + 1)
}

export interface ProjectWeek { week: number; label: string; donePts: number; cumPct: number }
export interface ProjectProgress {
  start: string
  finish: string | null
  totalPts: number
  donePts: number
  pct: number
  tasksTotal: number
  tasksDone: number
  openUndated: number
  elapsedPct: number | null // % waktu berlalu start→finish
  onPace: boolean | null // pct >= elapsedPct
  weeks: ProjectWeek[]
}

export const projectProgress = (
  project: Pick<Project, 'startDate' | 'createdAt' | 'deadline'>,
  tasks: Task[],
  statusOf: StatusOf,
  todayISO: string,
): ProjectProgress => {
  const start = isoDay(project.startDate) || isoDay(project.createdAt) || isoDay(todayISO)
  const finish = isoDay(project.deadline) || null
  const today = isoDay(todayISO)
  const totalPts = tasks.reduce((a, t) => a + pointsOf(t), 0)
  const donePts = tasks.reduce((a, t) => a + (statusOf(t) === 'done' ? pointsOf(t) : 0), 0)
  const tasksDone = tasks.filter((t) => statusOf(t) === 'done').length
  const openUndated = tasks.filter((t) => statusOf(t) !== 'done' && !isoDay(t.dueDate)).length

  let end = finish || today
  // Perpanjang hanya oleh tanggal SELESAI task yang sudah done (bukan dueDate open yang bisa typo).
  for (const t of tasks) {
    if (statusOf(t) !== 'done') continue
    const cd = isoDay(t.tanggalSelesai) || isoDay(t.dueDate)
    if (cd && cd > end) end = cd
  }
  // Clamp: maksimal ~1 tahun dari mulai → tanggal typo tak bisa bikin ribuan minggu.
  const maxEnd = addDays(start, 371)
  if (end > maxEnd) end = maxEnd
  const numWeeks = Math.min(53, Math.max(1, Math.ceil((diffDays(start, end) + 1) / 7)))
  const weeks: ProjectWeek[] = []
  let cum = 0
  for (let w = 1; w <= numWeeks; w++) {
    const ws = addDays(start, (w - 1) * 7)
    const we = addDays(ws, 6)
    let wp = 0
    for (const t of tasks) {
      if (statusOf(t) !== 'done') continue
      const cd = isoDay(t.tanggalSelesai) || isoDay(t.dueDate) || start
      if (cd >= ws && cd <= we) wp += pointsOf(t)
    }
    cum += wp
    weeks.push({ week: w, label: ws.slice(5), donePts: wp, cumPct: totalPts ? Math.round((cum / totalPts) * 100) : 0 })
  }

  const elapsedPct = finish && diffDays(start, finish) > 0
    ? Math.max(0, Math.min(100, Math.round((diffDays(start, today) / diffDays(start, finish)) * 100)))
    : null
  const pct = totalPts ? Math.round((donePts / totalPts) * 100) : 0
  const onPace = elapsedPct == null ? null : pct >= elapsedPct

  return { start, finish, totalPts, donePts, pct, tasksTotal: tasks.length, tasksDone, openUndated, elapsedPct, onPace, weeks }
}
